import {
  Failure,
  getId,
  maybeAppendCopyToText,
  Success,
  uniqueBy,
} from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { list, pluralize } from "../ui/fmt.ts";
import { EditMode } from "./EditMode.ts";
import { GangMode } from "./GangMode.ts";
import { Git } from "./Git.ts";
import type { Ruleset } from "./Ruleset.ts";
import { Vehicle } from "./Vehicle.ts";

export type VehicleNotFound = {
  type: "VEHICLE_NOT_FOUND";
};

export type GitNotFound = {
  type: "GIT_NOT_FOUND";
};

export type GangPermissions = Extract<keyof GangModePermissions, "models">;

export type GitPermissions = Exclude<
  keyof GangModePermissions,
  "mods" | "repairs"
>;

export type VehiclePermissions = Exclude<
  keyof GangModePermissions,
  "gear" | "treatments"
>;

export type GangModePermissions = {
  weapons: EditMode;
  gear: EditMode;
  treatments: EditMode;
  mods: EditMode;
  repairs: EditMode;
  models: EditMode;
};

export class Gang {
  ruleset: Ruleset;
  data: SpaceGits.GangData;

  static FALLBACK_NAME = "Unnamed Gang";

  constructor(ruleset: Ruleset, data: SpaceGits.GangData) {
    this.ruleset = ruleset;
    this.data = data;
  }

  get id() {
    return this.data.id;
  }

  get name() {
    return this.data.name.trim() || Gang.FALLBACK_NAME;
  }

  get description() {
    return this.data.description.trim();
  }

  get mode() {
    return new GangMode(this);
  }

  can(permission: EditMode, subject: GangPermissions) {
    return !!(this.mode.permissions[subject] & permission);
  }

  get modelsSummary() {
    const { gits, vehicles } = this;

    return list([
      gits.length > 0
        ? pluralize(gits.length, { one: "# git", other: "# gits" })
        : "no gits",

      vehicles.length > 0
        ? pluralize(vehicles.length, { one: "# vehicle", other: "# vehicles" })
        : null,
    ]);
  }

  get summary() {
    return `${this.reputation} rep · ${this.modelsSummary}`;
  }

  get allGits() {
    return this.data.gits
      .map((data) => new Git(this, data))
      .sort((left, right) => left.compare(right));
  }

  get gits() {
    return this.allGits.filter((git) => !git.isDead);
  }

  get allVehicles() {
    return this.data.vehicles
      .map((data) => new Vehicle(this, data))
      .sort((left, right) => left.compare(right));
  }

  get vehicles() {
    return this.allVehicles.filter((vehicle) => !vehicle.isWriteOff);
  }

  get hasVehicle() {
    return this.vehicles.length > 0;
  }

  get hasSomeDrivers() {
    return this.gits.some((git) => git.type.canDrive);
  }

  get models() {
    return [...this.gits, ...this.vehicles];
  }

  get cost() {
    return (
      this.gits.reduce((acc, git) => acc + git.cost, 0) +
      this.vehicles.reduce((acc, vehicle) => acc + vehicle.cost, 0)
    );
  }

  /**
   * The gang's models cost without any additional equipment.
   */
  get baseCost() {
    return (
      this.gits.reduce((acc, git) => acc + git.baseCost, 0) +
      this.vehicles.reduce((acc, vehicle) => acc + vehicle.baseCost, 0)
    );
  }

  get hasSomeLoadout() {
    return (
      this.data.gits.some((git) => {
        return (
          git.weapons.length > 0 ||
          git.gear.length > 0 ||
          git.treatments.length > 0
        );
      }) ||
      this.data.vehicles.some((vehicle) => {
        return (
          vehicle.weapons.length > 0 ||
          vehicle.mods.length > 0 ||
          vehicle.repairs.length > 0
        );
      })
    );
  }

  get purchasedLoadout() {
    return [
      ...this.weapons.filter((w) => w.cost > 0),
      ...this.gear,
      ...this.treatments,
      ...this.repairs,
      ...this.purchasedMods,
    ];
  }

  get reputation() {
    return this.gits.reduce((acc, git) => acc + git.attitude, 0);
  }

  get faction() {
    return this.data.faction
      ? this.ruleset.factions.get(this.data.faction.id)
      : null;
  }

  get factionRules() {
    return this.faction?.valueOrNull()?.rules ?? [];
  }

  get hasFaction() {
    return !!this.faction;
  }

  get gitRules() {
    return uniqueBy(
      this.gits
        .flatMap((g) => g.typeRules)
        .filter((l) => l.isSuccess)
        .map((l) => l.value),
      (item) => item.id,
    );
  }

  get weapons() {
    return [
      ...this.gits
        .flatMap((git) => git.weapons)
        .filter((lookup) => lookup.isSuccess)
        .map((lookup) => lookup.value),

      ...this.vehicles
        .flatMap((vehicle) => vehicle.weapons)
        .filter((lookup) => lookup.isSuccess)
        .map((lookup) => lookup.value),
    ];
  }

  get uniqueWeapons() {
    return uniqueBy(this.weapons, (item) => item.id);
  }

  get weaponSpecialRules() {
    return uniqueBy(
      this.uniqueWeapons
        .flatMap((w) => w.rules)
        .filter((l) => l.isSuccess)
        .map((l) => l.value),
      (item) => item.id,
    );
  }

  get gear() {
    return this.gits
      .flatMap((git) => git.gear)
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);
  }

  get uniqueGear() {
    return uniqueBy(this.gear, (item) => item.id);
  }

  get treatments() {
    return this.gits
      .flatMap((git) => git.treatments)
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);
  }

  get uniqueTreatments() {
    return uniqueBy(this.treatments, (item) => item.id);
  }

  get purchasedMods() {
    return this.vehicles
      .flatMap((vehicle) => vehicle.purchasedMods)
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);
  }

  get vehicleRules() {
    return this.vehicles
      .flatMap((vehicle) => vehicle.rules)
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);
  }

  get uniqueVehicleRules() {
    return uniqueBy(this.vehicleRules, (item) => item.id);
  }

  get mods() {
    return this.vehicles
      .flatMap((vehicle) => vehicle.mods)
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);
  }

  get uniqueMods() {
    return uniqueBy(this.mods, (item) => item.id);
  }

  get repairs() {
    return this.vehicles
      .flatMap((vehicle) => vehicle.repairs)
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);
  }

  get uniqueRepairs() {
    return uniqueBy(this.repairs, (item) => item.id);
  }

  get campaign() {
    return [
      ...(this.data.campaign ?? []),

      ...this.allGits.flatMap((git) =>
        git.campaign.map((item) => ({ ...item, gitId: git.id })),
      ),

      ...this.allVehicles.flatMap((vehicle) =>
        vehicle.campaign.map((item) => ({ ...item, vehicleId: vehicle.id })),
      ),
    ];
  }

  get stash() {
    const { ruleset } = this;

    const capDiffs = this.campaign.flatMap((item) => {
      switch (item.type) {
        case "STASH_MODIFIED": {
          return item.caps;
        }

        case "GIT_ADDED": {
          const costMultiplier = item.isDiscounted
            ? 1
            : ruleset.constants.campaignModeCostMultiplier;

          return (
            (ruleset.gits.get(item.gitType).valueOrNull()?.cost ?? 0) *
            costMultiplier *
            -1
          );
        }

        case "VEHICLE_ADDED": {
          return (
            (ruleset.vehicles.get(item.vehicleType).valueOrNull()?.cost ?? 0) *
            ruleset.constants.campaignModeCostMultiplier *
            -1
          );
        }

        case "GEAR_CHANGED": {
          return item.added.map(
            (ref) =>
              (ruleset.gear.get(ref.id).valueOrNull()?.cost ?? 0) *
              ruleset.constants.campaignModeCostMultiplier *
              -1,
          );
        }

        case "MODS_CHANGED": {
          return item.added.map(
            (ref) =>
              (ruleset.mods.get(ref.id).valueOrNull()?.cost ?? 0) *
              ruleset.constants.campaignModeCostMultiplier *
              -1,
          );
        }

        case "VEHICLE_WEAPONS_CHANGED":
        case "GIT_WEAPONS_CHANGED": {
          return item.updated.map(
            (ref) =>
              (ruleset.weapons.get(ref.id).valueOrNull()?.cost ?? 0) *
              ruleset.constants.campaignModeCostMultiplier *
              // Don't refund removed items
              Math.max(ref.count, 0) *
              -1,
          );
        }

        case "TREATMENTS_CHANGED": {
          if (!item.isPaid) {
            return 0;
          }
          return item.added.map((ref) => {
            return (
              (ruleset.treatmentLocations.get(ref.locationId).valueOrNull()
                ?.cost ?? 0) * -1
            );
          });
        }

        case "REPAIRS_CHANGED": {
          if (!item.isPaid) {
            return 0;
          }

          return item.added.map((ref) => {
            return (
              (ruleset.repairLocations.get(ref.locationId).valueOrNull()
                ?.cost ?? 0) * -1
            );
          });
        }

        default: {
          return 0;
        }
      }
    });

    return this.data.mode?.type === "CAMPAIGN"
      ? capDiffs.reduce(
          (acc, value) => acc + value,
          this.data.mode.initialStash,
        )
      : null;
  }

  getGit(id: string) {
    const git = this.allGits.find((git) => git.id === id);

    if (!git) {
      return new Failure<GitNotFound>({
        type: "GIT_NOT_FOUND",
      });
    }

    return new Success(git);
  }

  getVehicle(id: string) {
    const git = this.allVehicles.find((vehicle) => vehicle.id === id);

    if (!git) {
      return new Failure<VehicleNotFound>({
        type: "VEHICLE_NOT_FOUND",
      });
    }

    return new Success(git);
  }

  // I don't know how much I like this. It could be a getter, but we'd still
  // need to separate the origin so that it can be used on the server, and
  // it doesn't mesh nice with `useEnsureValue`.
  static getSnapshotLink(origin: string, snapshotId: string) {
    return `${origin}/gangs/s/${snapshotId}`;
  }

  /**
   * Creates a clone of the current gang and runs the Chaos Mode build mode.
   */
  runChaosMode() {
    const { mode } = this;
    const next = this.clone({
      gits: [],
      vehicles: [],
      mode: {
        type: "CHAOS_MODE",
        runCount: mode.runCount + 1,
        isCheatModeActive: mode.isCheatModeActive,
      },
    });

    next.addRandomModels();
    next.setRandomLoadout();
    next.nameUnnamedGits();

    return next;
  }

  /**
   * As an easter egg, we create a really rubbish loadout for the user if they
   * run the Chefs Rec build mode this many times.
   */
  static chefsRecRubbishRun = 50;

  /**
   * Creates a clone of the current gang and runs the Chef's Recommendation
   * build mode.
   */
  runChefsRec() {
    const { mode } = this;

    const next = this.clone({
      mode: {
        type: "CHEFS_REC",
        runCount: mode.runCount + 1,
        isCheatModeActive: mode.isCheatModeActive,
      },
    });

    if (next.mode.runCount === Gang.chefsRecRubbishRun) {
      return next.setRubbishLoadout();
    }

    return next.setRandomLoadout().nameUnnamedGits();
  }

  addRandomModels() {
    while (this.cost <= this.ruleset.constants.chaosModeCostThreshold) {
      const model = this.ruleset.getRandomModelType();

      if (model.type === "VEHICLE") {
        // If we don't have a vehicle yet add one
        if (!this.hasVehicle) {
          const vehicleData = Vehicle.initData({
            type: { id: model.vehicleType.id },
          });

          this.data.vehicles.push(vehicleData);
          continue;
        }

        // Otherwise, grant a bullorc or a klankbot, depending or whether
        // there is a driver-type git already present.
        const gitData = Git.initData({
          type: { id: this.hasSomeDrivers ? "klankbot" : "bullorc" },
        });

        this.data.gits.push(gitData);
        continue;
      }

      const gitData = Git.initData({
        type: { id: model.gitType.id },
      });

      this.data.gits.push(gitData);
    }

    return this;
  }

  private setRubbishLoadout() {
    for (const git of this.gits) {
      git.lobotomize();
    }

    for (const vehicle of this.vehicles) {
      vehicle.strip();
    }

    return this;
  }

  private setRandomLoadout() {
    for (const git of this.gits) {
      if (!git.isAnimal) {
        git.setRandomLoot({ count: 2 });
        git.setRandomTreatment({ count: 1 });
      }
    }

    for (const vehicle of this.vehicles) {
      vehicle.setRandomLoot({ count: 2 });
      vehicle.setRandomRepairs({ count: 1 });
    }

    return this;
  }

  nameUnnamedGits() {
    for (const git of this.gits) {
      if (!git.isNamed) {
        git.setRandomName();
      }
    }

    return this;
  }

  /**
   * Creates a new gang with supplied caps added to its stash.
   *
   * Note that only gangs in Campaign Mode have a stash, and therefore the
   * gang will also be switched to Campaign Mode if it isn't already.
   */
  modifyStash(caps: number) {
    return this.clone({
      campaign: [
        ...(this.data.campaign ?? []),
        { type: "STASH_MODIFIED", caps, createdTs: Date.now() },
      ],
    });
  }

  addGit(
    data: Pick<SpaceGits.GitData, "name" | "type">,
    isDiscounted: boolean,
  ) {
    const { mode } = this;
    const git = Git.initData(data);
    const gits: SpaceGits.GitData[] = [...this.data.gits, git];

    if (mode.isCampaignMode && !mode.isCheatModeActive) {
      return this.clone({
        campaign: [
          ...(this.data.campaign ?? []),
          {
            type: "GIT_ADDED",
            isDiscounted,
            gitId: git.id,
            gitType: git.type.id,
            createdTs: Date.now(),
          },
        ],
        gits,
      })
        .getGit(git.id)
        .valueOrThrow();
    }

    return this.clone({ gits }).getGit(git.id).valueOrThrow();
  }

  duplicateGit(git: Git) {
    const dupe: SpaceGits.GitData = {
      ...git.data,
      id: getId(),
      name: maybeAppendCopyToText(git.data.name),
    };

    return this.clone({ gits: [...this.data.gits, dupe] })
      .getGit(dupe.id)
      .valueOrThrow();
  }

  duplicateVehicle(vehicle: Vehicle) {
    const dupe: SpaceGits.VehicleData = {
      ...vehicle.data,
      id: getId(),
      name: maybeAppendCopyToText(vehicle.data.name),
    };

    return this.clone({ vehicles: [...this.data.vehicles, dupe] })
      .getVehicle(dupe.id)
      .valueOrThrow();
  }

  removeGit(git: Git) {
    return this.clone({
      gits: this.data.gits.filter((data) => data.id !== git.id),
    });
  }

  removeVehicle(vehicle: Vehicle) {
    return this.clone({
      vehicles: this.data.vehicles.filter((v) => v.id !== vehicle.id),
    });
  }

  addVehicle(data: Pick<SpaceGits.VehicleData, "name" | "type">) {
    const { mode } = this;
    const vehicle = Vehicle.initData(data);
    const vehicles: SpaceGits.VehicleData[] = [...this.data.vehicles, vehicle];

    if (mode.isCampaignMode && !mode.isCheatModeActive) {
      return this.clone({
        campaign: [
          ...(this.data.campaign ?? []),
          {
            type: "VEHICLE_ADDED",
            vehicleId: vehicle.id,
            vehicleType: vehicle.type.id,
            createdTs: Date.now(),
          },
        ],
        vehicles,
      })
        .getVehicle(vehicle.id)
        .valueOrThrow();
    }

    return this.clone({ vehicles }).getVehicle(vehicle.id).valueOrThrow();
  }

  clone(data?: Partial<SpaceGits.GangData>) {
    return new Gang(this.ruleset, { ...this.data, ...data });
  }

  static initData(data: {
    name?: string;
    description?: string;
    rulesetVersion: string;
    mode?: SpaceGits.GangData["mode"];
    faction?: SpaceGits.GangData["faction"];
    gits?: SpaceGits.GangData["gits"];
    vehicles?: SpaceGits.GangData["vehicles"];
  }): SpaceGits.GangData {
    const now = Date.now();

    return {
      id: getId(),
      name: data.name ?? "",
      description: data.description ?? "",
      mode: data.mode,
      rulesetVersion: data.rulesetVersion,
      snapshotId: null,
      faction: data.faction ?? null,
      gits: data.gits ?? [],
      vehicles: data.vehicles ?? [],
      createdTs: now,
      updatedTs: now,
      deleted: false,
    };
  }
}
